import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dismissRun, getDesk, type DeskFinishedRun, type DeskResponse, type DeskRow, type DeskSlot, type DeskWriting, type SentRow } from './api'
import { chipClass, destinationChipClass, destinationLabel, etDate, etDayShort, HANDOFF, stageLabel, etTime } from './labels'
import { canPublish, useEditorialMe } from './me'
import { useCanDraft, useEditorialTitle } from './titleContext'
import { ACTION_LABEL, deskPollMs, finishedRunHeading, formatLabel, greeting, IDEAS_UNAVAILABLE, minutesIn, needsCount, piecesLanded, slotWhen, writingSteps } from './desk'
import { dayLabel, membersGainedText, pctOrDash, people, signUps } from './readers'
import { actionHref, pieceHref, piecesHref, sentPiecesHref, startHref, startIdea } from './links'
import { describeError } from '../lib/describeError'
import { FeedbackDialog } from './FeedbackDialog'
import { notesLabel } from './feedback'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 ${focus}`
const chipPlain = 'inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-semibold text-paper-dim'
const link = `text-[13px] text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`

const TILE_DOT = {
  writing: 'bg-radar-cyan', check_sources: 'bg-radar-violet', to_review: 'bg-amber-glow',
  needs_a_look: 'bg-amber-deep', approved: 'bg-radar-green', scheduled: 'bg-radar-cyan', live: 'bg-radar-green',
}

function slotTitle(s: DeskSlot): string {
  return s.title ?? 'Nothing queued for this slot'
}

function Tile({ label, count, note, dot, to }: { label: string; count: number; note: string; dot: string; to?: string }) {
  const body = (
    <>
      <span className="flex items-center gap-2 font-ui text-[13px] font-semibold text-paper-dim">
        <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
        {label}
      </span>
      <span className="font-serif text-[34px] leading-none text-paper">{count}</span>
      <span className="font-ui text-[12px] text-paper-faint">{note}</span>
    </>
  )
  const cls = `${card} flex flex-col gap-1.5 px-4 py-3.5`
  if (!to) return <div className={cls}>{body}</div>
  return <Link to={to} className={`${cls} no-underline hover:bg-ink-600 ${focus}`}>{body}</Link>
}

function NeedsCard({ row, section, path }: { row: DeskRow; section: string; path: string }) {
  const href = actionHref(path, row.action, row.id)
  const urgent = row.status === 'needs_attention' || row.status === 'failed'
  return (
    <article className={`${card} flex flex-wrap items-center gap-x-5 gap-y-3.5 px-[18px] py-4`}>
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-2">
        <div className="flex flex-wrap gap-1.5">
          <span className={chipClass(row.status)}>{stageLabel(row.status)}</span>
          <span className={destinationChipClass(row.destination)}>{destinationLabel(row.destination, section)}</span>
          <span className={chipPlain}>{formatLabel(row.format)}</span>
        </div>
        <Link to={href} className={`font-serif text-[21px] leading-tight text-paper no-underline hover:underline ${focus}`}>{row.title}</Link>
        {row.reason && <p className={`m-0 font-ui text-[13px] ${urgent ? 'text-amber-glow' : 'text-paper-dim'}`}>{row.reason}</p>}
      </div>
      <Link to={href} className={row.action === 'review' ? btnPrimary : btn} aria-label={`${ACTION_LABEL[row.action]}: ${row.title}`}>
        {ACTION_LABEL[row.action]}
      </Link>
    </article>
  )
}

/** Editors: "Sent by writers", every piece a writer handed over. Writers: their own, "Waiting for an editor". */
function SentCard({ rows, path, editor }: { rows: SentRow[]; path: string; editor: boolean }) {
  const heading = editor ? HANDOFF.deskEditors : HANDOFF.deskWriter
  return (
    <section aria-labelledby="desk-sent" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="desk-sent" className="label m-0">{heading}</h2>
        {rows.length > 0 && <Link to={sentPiecesHref(path)} className={link}>{HANDOFF.filter}</Link>}
      </div>
      {rows.length === 0 && <p className="m-0 font-ui text-[13px] text-paper-dim">Nothing is with an editor.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex flex-col gap-1 border-t border-ink-500 pt-2.5 font-ui">
          <Link to={pieceHref(path, r.id)} className={`text-[14px] font-semibold leading-snug text-paper no-underline hover:underline ${focus}`}>{r.title}</Link>
          <span className="text-[12px] text-paper-faint">
            {editor && r.by_name ? `${r.by_name} · ` : ''}{etDate(r.sent_at)}, {etTime(r.sent_at)}
          </span>
        </div>
      ))}
    </section>
  )
}

function WritingNow({ w, section }: { w: NonNullable<DeskResponse['writing']>; section: string }) {
  const steps = writingSteps(w.stage)
  const mins = minutesIn(w.started_at)
  return (
    <section aria-labelledby="desk-writing" className={`${card} flex flex-col gap-3.5 p-[18px]`}>
      <div className="flex items-center justify-between gap-2">
        <h2 id="desk-writing" className="label m-0">Writing now</h2>
        {mins !== null && (
          <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-radar-cyan/15 px-2.5 font-ui text-[12px] font-semibold text-radar-cyan">
            <span className="h-[7px] w-[7px] rounded-full bg-radar-cyan" aria-hidden="true" />
            {mins} min in
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-serif text-[19px] leading-tight text-paper">{w.topic_label ?? 'The best open subject'}</span>
        <span className="font-ui text-[13px] text-paper-faint">
          {formatLabel(w.format)} · {destinationLabel(w.destination, section)}{w.requested_by ? ` · asked for by ${w.requested_by}` : ''}
        </span>
      </div>
      <ol className="m-0 flex list-none flex-col gap-2 p-0 font-ui text-[13px]">
        {steps.map((s) => (
          <li key={s.label} aria-current={s.state === 'now' ? 'step' : undefined}
              className={`flex items-center gap-2.5 ${s.state === 'todo' ? 'text-paper-faint' : s.state === 'now' ? 'text-paper' : 'text-paper-dim'}`}>
            <span aria-hidden="true" className={`h-4 w-4 flex-none rounded-full ${
              s.state === 'done' ? 'bg-radar-cyan' : s.state === 'now' ? 'border-2 border-radar-cyan bg-ink-700' : 'border-2 border-ink-400'}`} />
            {s.label}
            <span className="sr-only">{s.state === 'done' ? ' (done)' : s.state === 'now' ? ' (in progress)' : ' (waiting)'}</span>
          </li>
        ))}
      </ol>
      <p className="m-0 font-ui text-[12px] text-paper-faint">
        Usually about {w.estimate_minutes} minutes for {formatLabel(w.format)}. You can leave this page; it shows here when it is ready to review.
      </p>
    </section>
  )
}

function FinishedRun({ r, path, publicationSlug }: { r: DeskFinishedRun; path: string; publicationSlug: string }) {
  const qc = useQueryClient()
  const dismiss = useMutation({
    mutationFn: () => dismissRun(publicationSlug, r.run_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['editorial-desk', publicationSlug] }),
  })
  const ok = r.status === 'done'
  const headingId = `desk-run-${r.run_id}`
  return (
    <section aria-labelledby={headingId}
             className={`${card} flex flex-col gap-3 p-[18px] ${ok ? 'border-radar-green/60' : 'border-amber-deep/60'}`}>
      <div className="flex items-center justify-between gap-2">
        <h2 id={headingId} className="label m-0">{finishedRunHeading(r)}</h2>
        <span className={`inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 font-ui text-[12px] font-semibold ${ok ? 'bg-radar-green/15 text-radar-green' : 'bg-amber-deep/15 text-amber-glow'}`}>
          <span className={`h-[7px] w-[7px] rounded-full ${ok ? 'bg-radar-green' : 'bg-amber-glow'}`} aria-hidden="true" />
          {r.finished_at ? etTime(r.finished_at) : ''}
        </span>
      </div>
      {!ok && <span className="font-serif text-[19px] leading-tight text-paper">{r.topic_label ?? 'A piece'}</span>}
      {!ok && r.error && <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">{r.error}</p>}
      {r.pieces.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3">
          <span className={ok ? 'font-serif text-[19px] leading-tight text-paper' : 'font-ui text-[13px] text-paper-dim'}>{ok ? p.title : 'What it gathered is kept on the piece.'}</span>
          <Link to={pieceHref(path, p.id)} className={ok ? btnPrimary : btn}>Open</Link>
        </div>
      ))}
      {!ok && r.pieces.length === 0 && (
        <div className="flex justify-end">
          <button type="button" className={btn} disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>Dismiss</button>
        </div>
      )}
      {dismiss.isError && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">Could not dismiss it. {describeError(dismiss.error)}</p>}
    </section>
  )
}

function GoingOut({ slots, releasesOn, section }: { slots: DeskSlot[]; releasesOn: boolean; section: string }) {
  const now = new Date()
  return (
    <section aria-labelledby="desk-next" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <h2 id="desk-next" className="label m-0">Going out next</h2>
      {!releasesOn && slots.length > 0 && (
        <p role="status" className="m-0 rounded-lg bg-amber-glow/20 px-3 py-2 font-ui text-[13px] font-semibold text-amber-glow">Releases are paused.</p>
      )}
      {slots.length === 0 && <p className="m-0 font-ui text-[13px] text-paper-dim">Nothing is scheduled.</p>}
      {slots.map((s) => (
        <div key={s.at} className="grid grid-cols-[72px_1fr] items-start gap-3 border-t border-ink-500 pt-2.5 font-ui">
          {slotWhen(s.at, now) ? (
            <span className="flex flex-col text-[12px] leading-snug text-paper-faint">
              <strong className="text-[13px] font-bold text-paper">{etDayShort(s.at)}</strong>
              {etTime(s.at)}
            </span>
          ) : (
            <span className="text-[13px] font-bold leading-snug text-paper">In the next few minutes</span>
          )}
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[14px] font-semibold leading-snug text-paper">{slotTitle(s)}</span>
            {s.destination && <span className={`${destinationChipClass(s.destination)} self-start`}>{destinationLabel(s.destination, section)}</span>}
          </span>
        </div>
      ))}
    </section>
  )
}

function RecentCard({ rows, path, since, feedbackTotal, onFeedback }: {
  rows: DeskResponse['recent']; path: string; since: string | null; feedbackTotal: number
  onFeedback: (pieceId: number | null) => void
}) {
  return (
    <section aria-labelledby="desk-recent" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="desk-recent" className="label m-0">How recent pieces did</h2>
        <button type="button" onClick={() => onFeedback(null)}
                className={`inline-flex min-h-8 items-center gap-2 rounded-full border border-ink-400 px-3 font-ui text-[12px] font-semibold text-paper hover:border-amber-glow hover:bg-ink-600 ${focus}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-amber-glow" aria-hidden="true" />
          Reader feedback
          <span className="font-serif text-[15px] leading-none text-amber-glow">{feedbackTotal}</span>
        </button>
      </div>
      {rows.length === 0 && <p className="m-0 font-ui text-[13px] text-paper-dim">Nothing published in the last 30 days.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex flex-col gap-1 border-t border-ink-500 pt-2.5 font-ui">
          <Link to={pieceHref(path, r.id)} className={`text-[14px] font-semibold leading-snug text-paper no-underline hover:underline ${focus}`}>{r.title}</Link>
          <span className="text-[12px] text-paper-faint">
            {people(r.people)} · {pctOrDash(r.finished_pct)} finished · {r.source === 'desk' ? membersGainedText(r.members_gained ?? 0) : signUps(r.signups_after)}
            {r.feedback > 0 && (
              <> · <button type="button" onClick={() => onFeedback(r.id)} aria-label={`${notesLabel(r.feedback)} from readers: ${r.title}`}
                           className={`rounded font-semibold text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>{notesLabel(r.feedback)}</button></>
            )}
          </span>
        </div>
      ))}
      {rows.length > 0 && <p className="m-0 font-ui text-[12px] text-paper-faint">Pieces in two places add both copies.</p>}
      {since && <p className="m-0 font-ui text-[12px] text-paper-faint">Last 30 days. Counting since {since}.</p>}
    </section>
  )
}

export function DeskPage() {
  const { section, publicationSlug } = useEditorialTitle()
  const canDraft = useCanDraft()
  const me = useEditorialMe()
  const editor = canPublish(me.data)
  // undefined: closed; null: the whole title; a number: one piece
  const [feedbackFor, setFeedbackFor] = useState<number | null | undefined>(undefined)
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['editorial-desk', publicationSlug],
    queryFn: () => getDesk(publicationSlug),
    refetchInterval: (query) => deskPollMs(query.state.data),
  })
  // A piece landed (the run ended, or one version of several was saved): the piece lists reload,
  // so To review already has it when the card says it is ready.
  const wasWriting = useRef<DeskWriting | null | undefined>(undefined)
  const writing = q.data?.writing
  useEffect(() => {
    if (q.data === undefined) return
    if (piecesLanded(wasWriting.current, writing ?? null)) qc.invalidateQueries({ queryKey: ['editorial-pieces'] })
    wasWriting.current = writing ?? null
  }, [q.data, writing, qc])
  if (q.isPending) return <p role="status" className="font-ui text-[14px] text-paper-dim">Loading the desk…</p>
  if (q.isError || !q.data) return <p role="alert" className="font-ui text-[14px] text-radar-red">Could not load the desk. {describeError(q.error)}</p>
  const d = q.data
  const c = d.counts
  const n = d.needs_you.length
  const nextSlot = d.next_slots[0]
  return (
    <div className="flex flex-col gap-7 font-ui text-paper">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="label">{section.label} · Desk</span>
          <h1 className="m-0 font-serif text-[40px] font-medium leading-[1.05] tracking-tight text-paper">{greeting(needsCount(d))}</h1>
          <p className="m-0 max-w-[640px] text-[14px] text-paper-dim">
            {editor || me.isPending ? 'Nothing goes anywhere until you publish it.' : 'Nothing goes anywhere until an editor publishes it.'}
          </p>
        </div>
        {canDraft && <Link to={startHref(section.path)} className={btnPrimary}>Start a piece</Link>}
      </header>

      <section aria-label="Where every piece is" className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-2.5">
        <Tile label="Writing" count={c.writing} note={d.writing?.topic_label ?? 'Nothing running'} dot={TILE_DOT.writing} />
        <Tile label="Check sources" count={c.check_sources} note="Waiting for you" dot={TILE_DOT.check_sources} to={piecesHref(section.path, 'check_sources')} />
        <Tile label="Ready for review" count={c.to_review} note="Checked and ready" dot={TILE_DOT.to_review} to={piecesHref(section.path, 'to_review')} />
        <Tile label="Needs a look" count={c.needs_a_look} note="Something to fix first" dot={TILE_DOT.needs_a_look} to={piecesHref(section.path, 'to_review')} />
        <Tile label="Approved" count={c.approved ?? 0} note="Ready to schedule or publish" dot={TILE_DOT.approved} to={piecesHref(section.path, 'approved')} />
        <Tile label="Scheduled" count={c.scheduled} note={nextSlot ? `Next: ${etDayShort(nextSlot.at)}` : 'Nothing scheduled'} dot={TILE_DOT.scheduled} to={piecesHref(section.path, 'scheduled')} />
        <Tile label="Live this month" count={c.live_this_month} note="Published since the 1st" dot={TILE_DOT.live} to={piecesHref(section.path, 'live')} />
      </section>

      <div className="flex flex-wrap items-start gap-5">
        <section aria-labelledby="desk-needs" className="flex min-w-0 flex-[2_1_520px] flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="desk-needs" className="m-0 font-serif text-[26px] font-medium text-paper">Needs you</h2>
            <Link to={piecesHref(section.path)} className={link}>All pieces</Link>
          </div>
          {editor && (d.sent_to_editor ?? []).length > 0 && <SentCard rows={d.sent_to_editor ?? []} path={section.path} editor />}
          {n === 0 && <p className={`${card} m-0 px-[18px] py-4 text-[14px] text-paper-dim`}>Nothing needs you right now.</p>}
          {d.needs_you.map((row) => (
            // a writer cannot publish the public copy: the piece just opens
            <NeedsCard key={row.id} section={section.label} path={section.path}
                       row={!editor && row.action === 'publish_public_copy' ? { ...row, action: 'review' } : row} />
          ))}
        </section>

        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-5">
          {me.data && !editor && <SentCard rows={d.sent_to_editor ?? []} path={section.path} editor={false} />}
          {d.writing && <WritingNow w={d.writing} section={section.label} />}
          {(d.finished_runs ?? []).map((r) => <FinishedRun key={r.run_id} r={r} path={section.path} publicationSlug={publicationSlug} />)}
          <RecentCard rows={d.recent ?? []} path={section.path} since={d.tracking_since ? dayLabel(d.tracking_since.slice(0, 10)) : null}
                      feedbackTotal={d.feedback_total ?? 0} onFeedback={setFeedbackFor} />
          <GoingOut slots={d.next_slots} releasesOn={d.releases_on} section={section.label} />
        </div>
      </div>

      {feedbackFor !== undefined && (
        <FeedbackDialog publicationSlug={publicationSlug} sectionLabel={section.label} sectionPath={section.path}
                        pieceId={feedbackFor} showInbox={me.data?.is_admin === true} editor={editor} onClose={() => setFeedbackFor(undefined)} />
      )}

      {d.ideas_error && <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">{IDEAS_UNAVAILABLE}</p>}
      {d.ideas.length > 0 && (
        <section aria-labelledby="desk-ideas" className="flex flex-col gap-3">
          <h2 id="desk-ideas" className="m-0 font-serif text-[26px] font-medium text-paper">Worth writing this week</h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-3.5">
            {d.ideas.map((i) => (
              <article key={i.label} className={`${card} flex flex-col gap-2.5 p-[18px]`}>
                <h3 className="m-0 font-serif text-[20px] font-medium leading-tight text-paper">{i.label}</h3>
                <p className="m-0 text-[13px] text-paper-dim">Readers ask: {i.ask}</p>
                {i.reasons.length > 0 && (
                  <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                    {i.reasons.map((r) => <li key={r} className={chipPlain}>{r}</li>)}
                  </ul>
                )}
                {canDraft && <Link to={startIdea(section.path, i.label)} className={`${btn} mt-1 self-start`}>Start a piece</Link>}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
